export type DrawerEventType =
  | "opening_balance"
  | "cash_sale"
  | "cash_refund"
  | "paid_in"
  | "paid_out"
  | "no_sale"
  | "manual_open"
  | "reconciliation";

export interface DrawerEvent {
  id: string;
  timestamp: string;
  type: DrawerEventType;
  user: string;
  role: string;
  amount?: number;
  reason?: string;
  note?: string;
  transactionId?: string;
  outletId?: number | null;
  counterId?: number | null;
}

export interface DrawerSummary {
  store: string;
  lane: string;
  cashier: string;
  shift: string;
  status: "ready" | "open" | "error" | "locked";
  openingBalance: number;
  cashSales: number;
  cashRefunds: number;
  paidIn: number;
  paidOut: number;
  expectedBalance: number;
}

const DRAWER_EVENTS_KEY = "posDrawerEvents";

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseJson = (value: string | null) => {
  if (!value) return {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const readAllEvents = (): DrawerEvent[] => {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(DRAWER_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAllEvents = (events: DrawerEvent[]) => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(DRAWER_EVENTS_KEY, JSON.stringify(events));
};

const getCurrentContext = () => {
  const counter = parseJson(localStorage.getItem("counter"));
  const outlet = parseJson(localStorage.getItem("outlet"));
  const counterSession = parseJson(localStorage.getItem("counterSession"));
  const checkedInInfo = parseJson(localStorage.getItem("counterCheckedInInfo"));
  const checkIn = parseJson(sessionStorage.getItem("posCheckIn"));
  const userInfo = parseJson(localStorage.getItem("userInfo"));

  const cashier =
    counterSession?.user?.name ??
    checkIn?.user?.name ??
    userInfo?.name ??
    "Cashier";
  const store =
    checkIn?.outlet?.name ??
    outlet?.name ??
    checkedInInfo?.outletName ??
    "Outlet";
  const lane =
    checkIn?.counter?.name ??
    counter?.name ??
    checkedInInfo?.counterName ??
    "Lane";
  const sessionStart =
    counterSession?.startTime ??
    checkIn?.timestamp ??
    new Date().toISOString();
  const hour = new Date(sessionStart).getHours();
  const shift =
    hour < 12 ? "Morning Shift" : hour < 18 ? "Afternoon Shift" : "Evening Shift";

  return {
    outletId: toNumber(outlet?.id ?? checkIn?.outlet?.id) || null,
    counterId: toNumber(counter?.id ?? checkIn?.counter?.id) || null,
    cashier,
    store: store || "Outlet",
    lane: lane || "Lane",
    shift,
    status: counterSession?.id ? ("open" as const) : ("ready" as const),
    openingBalance:
      toNumber(checkIn?.openingBalance?.amount) ||
      toNumber(parseJson(sessionStorage.getItem("posOpeningBalance"))?.amount),
  };
};

export const appendDrawerEvent = (
  event: Omit<DrawerEvent, "id" | "timestamp" | "outletId" | "counterId"> &
    Partial<Pick<DrawerEvent, "timestamp" | "outletId" | "counterId">>
) => {
  const context = getCurrentContext();
  const timestamp = event.timestamp ?? new Date().toISOString();
  const nextEvent: DrawerEvent = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp,
    type: event.type,
    user: event.user,
    role: event.role,
    amount: event.amount,
    reason: event.reason,
    note: event.note,
    transactionId: event.transactionId,
    outletId: event.outletId ?? context.outletId,
    counterId: event.counterId ?? context.counterId,
  };

  const events = readAllEvents();
  events.push(nextEvent);
  writeAllEvents(events);
};

export const getDrawerDetailsForToday = (): {
  drawerInfo: DrawerSummary;
  events: DrawerEvent[];
} => {
  const context = getCurrentContext();
  const todayKey = getDayKey(new Date());

  const events = readAllEvents()
    .filter((event) => {
      const eventDay = getDayKey(new Date(event.timestamp));
      const matchesDay = eventDay === todayKey;
      const matchesOutlet =
        context.outletId == null || event.outletId == null || event.outletId === context.outletId;
      const matchesCounter =
        context.counterId == null ||
        event.counterId == null ||
        event.counterId === context.counterId;
      return matchesDay && matchesOutlet && matchesCounter;
    })
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

  const openingBalanceFromEvent =
    events.find((event) => event.type === "opening_balance")?.amount ?? 0;
  const openingBalance =
    toNumber(openingBalanceFromEvent) || toNumber(context.openingBalance);

  const cashSales = events
    .filter((event) => event.type === "cash_sale")
    .reduce((sum, event) => sum + Math.max(0, toNumber(event.amount)), 0);
  const cashRefunds = events
    .filter((event) => event.type === "cash_refund")
    .reduce((sum, event) => sum + Math.abs(toNumber(event.amount)), 0);
  const paidIn = events
    .filter((event) => event.type === "paid_in")
    .reduce((sum, event) => sum + Math.max(0, toNumber(event.amount)), 0);
  const paidOut = events
    .filter((event) => event.type === "paid_out")
    .reduce((sum, event) => sum + Math.abs(toNumber(event.amount)), 0);

  const expectedBalance = openingBalance + cashSales + paidIn - cashRefunds - paidOut;

  return {
    drawerInfo: {
      store: context.store,
      lane: context.lane,
      cashier: context.cashier,
      shift: context.shift,
      status: context.status,
      openingBalance,
      cashSales,
      cashRefunds,
      paidIn,
      paidOut,
      expectedBalance,
    },
    events,
  };
};
